import type { RenderParams } from '@seek/forms-ui';
import { isEmail, maxLength, required } from '@seek/validators-js';
import {
  Box,
  Button,
  CheckboxStandalone,
  Column,
  Columns,
  IconArrow,
  Inline,
  RadioGroup,
  RadioItem,
  Stack,
  Text,
  TextField,
  TextLink,
} from 'braid-design-system';
import React from 'react';

import { UserRegistrationType } from 'src/generated/graphql';
import { mapBraidFieldProps } from 'src/utils/mapBraidFieldProps';

import { type FormValues, useField } from '../MarketplaceInsights';

const DEFAULT_CHARACTER_LIMIT = 250;

interface Props extends RenderParams<FormValues> {
  onSubmit: (formData: FormValues) => void;
  loading: boolean;
}

export const SignupForm = (props: Props) => {
  const firstNameField = useField({
    id: 'firstName',
    validators: [required, maxLength],
    validatorProps: {
      requiredLabel: ' your first name.',
      max: DEFAULT_CHARACTER_LIMIT,
    },
  });
  const lastNameField = useField({
    id: 'lastName',
    validators: [required, maxLength],
    validatorProps: {
      requiredLabel: ' your last name.',
      max: DEFAULT_CHARACTER_LIMIT,
    },
  });
  const emailField = useField({
    id: 'email',
    validators: [required, maxLength, isEmail],
    validatorProps: {
      requiredLabel: ' your email.',
      max: DEFAULT_CHARACTER_LIMIT,
    },
  });
  const userTypeField = useField({
    id: 'userType',
    validators: [required],
    validatorProps: {
      requiredMessage: 'Please select from the following.',
    },
  });
  const checkbox = useField({
    id: 'checkbox',
  });
  const checkboxInvalid = checkbox.touched && checkbox.value === false;

  return (
    <form onSubmit={props.handleSubmit(props.onSubmit)}>
      <Stack space="medium">
        <TextField
          name="fields[first_name]"
          id="drip-first-name"
          label="First Name"
          characterLimit={DEFAULT_CHARACTER_LIMIT}
          {...mapBraidFieldProps(firstNameField)}
        />
        <TextField
          name="fields[last_name]"
          id="drip-last-name"
          label="Last Name"
          characterLimit={DEFAULT_CHARACTER_LIMIT}
          {...mapBraidFieldProps(lastNameField)}
        />
        <TextField
          name="fields[email]"
          id="drip-email"
          label="Email Address"
          characterLimit={DEFAULT_CHARACTER_LIMIT}
          {...mapBraidFieldProps(emailField)}
        />

        <RadioGroup
          name="fields[insights_report_signup]"
          id="drip-insights-report-signup"
          label="Select from the following"
          {...mapBraidFieldProps(userTypeField)}
        >
          <RadioItem label="I am a Buyer" value={UserRegistrationType.Buyer} />
          <RadioItem
            label="I am a Private Seller"
            value={UserRegistrationType.PrivateSeller}
          />
          <RadioItem
            label="I am a Broker"
            value={UserRegistrationType.Broker}
          />
          <RadioItem
            label="I am a Franchisor"
            value={UserRegistrationType.Franchisor}
          />
          <RadioItem
            label="I am a small to medium-sized enterprise (SME)"
            value={UserRegistrationType.Other}
          />
        </RadioGroup>

        <Box paddingTop="medium">
          <Columns space="small">
            <Column width="content">
              <CheckboxStandalone
                id="agree-terms"
                aria-label="I have read Seek Business's Privacy Policy and Terms of Service"
                checked={checkbox.value}
                onChange={checkbox.onChange}
                tone={checkboxInvalid ? 'critical' : 'neutral'}
              />
            </Column>
            <Column>
              <Stack space="small">
                <Text weight={checkbox.value ? 'strong' : 'regular'}>
                  I have read Seek Business&apos;s{' '}
                  <TextLink
                    href="https://www.seekbusiness.com.au/privacy-policy"
                    target="_blank"
                  >
                    Privacy Policy
                  </TextLink>{' '}
                  and{' '}
                  <TextLink
                    href="https://www.seekbusiness.com.au/terms-and-conditions"
                    target="_blank"
                  >
                    Terms of Service
                  </TextLink>
                  .
                </Text>
                {checkboxInvalid && (
                  <Text tone="critical">
                    <i>
                      You must tick the checkbox in order to accept these terms.
                    </i>
                  </Text>
                )}
              </Stack>
            </Column>
          </Columns>
        </Box>
        <Inline space="none">
          <Button
            data={{ 'data-drip-attribute': 'sign-up-button' }}
            type="submit"
            loading={props.loading}
          >
            Get your free report <IconArrow direction="right" />
          </Button>
        </Inline>
      </Stack>
    </form>
  );
};
